import {jsx, ParamComponent} from 'panel';
import moment from 'moment';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {TableEl} from 'common/components/table';
import {TableColumn} from 'common/components/table/types';
import {shouldOpenInNewTab} from 'common/util/dom';
import {DummyUrlManager} from 'common/contexts/url-manager/dummy';
import {IronContextRegistry} from 'common/report/context-registry';
import {RowEntity, TableField} from 'common/widgets/entity-collection/entity-table/types';
import {CellEntityNameWithIcon} from 'common/widgets/entity-collection/entity-table/cell-renderers';
import {appendBrowseSource, getDisplayContext} from 'common/report/util/search';
import {SearchResult, SearchResultType} from 'common/report/util/search/types';
import {BrowseMode} from 'common/widgets/browse/types';
import {DashboardType} from 'common/report/dashboards/types';

import {BrowseCreatorWithViews} from '../mixins';
import newTableStyleWithOverrides from './table.inline.styl';
import css from './index.inline.styl';

type EntityTableParams = {
  disableEntityIcons?: boolean;
  emptyMessage?: string;
  mode: BrowseMode;
  isLoading: boolean;
  nameLabel: string;
  userId: number;
  stickyTitles?: boolean;
  trackName?: string;
  entities: SearchResult[];
  onEntityClick?: (entity: SearchResult) => void;
  onEntitySelect?: (entity: SearchResult) => void;
  onEntityRedirect?: () => void;
};

export class EntityTable extends ParamComponent<EntityTableParams, EmptyObj, IronContextRegistry> {
  get config() {
    return {
      css,
      contexts: [`urlManager` as const, `currentProject` as const],
      params: {
        disableEntityIcons: Boolean,
        emptyMessage: String,
        mode: String,
        isLoading: Boolean,
        nameLabel: String,
        userId: Number,
        stickyTitles: Boolean,
        trackName: String,
        entities: Array,
        onEntityClick: Function,
        onEntitySelect: Function,
        onEntityRedirect: Function,
      },
      defaultContexts: {
        urlManager: new DummyUrlManager(),
      },
      useShadowDom: true,
      template: () => (
        <TableEl
          attrs={{
            loading: this.params.isLoading,
            'default-sort-field': ``,
            'empty-message': this.params.emptyMessage,
            'sticky-titles': this.params.stickyTitles,
            'disable-page-sticky-titles': this.params.stickyTitles,
            'hide-titles': true,
            'style-override': newTableStyleWithOverrides,
            elref: `entityTable`,
            ...(this.params.mode === BrowseMode.Add ? {'max-body-height': `450px`} : {'window-scroll': true}),
          }}
          props={{
            rows: this.getTableRows(this.params.entities),
            columns: this.getTableColumns(),
          }}
          on={{
            rowClick: (ev) => this.onRowSelect(ev),
          }}
        />
      ),
    };
  }

  getTableColumns(): TableColumn[] {
    return [
      {
        field: TableField.Name,
        flexGrow: `4`,
        flexBasis: `260px`,
        isSearchable: true,
        isSortable: false,

        cellRenderer: (entity) => (
          <CellEntityNameWithIcon
            entity={entity}
            skipEntityLinks={this.params.mode === BrowseMode.Add}
            skipEntityIcon={!!this.params.disableEntityIcons}
          />
        ),
      },
      {
        field: TableField.LastModified,
        flexGrow: `3`,
        flexBasis: `130px`,
        isSearchable: true,
        isSortable: false,
        labelTransform: (entity) => {
          const modifiedAsMoment = moment.utc(entity.lastModified).local();
          return `Last edited ${modifiedAsMoment.fromNow()}`;
        },
      },
      {
        field: `uniqueViews`,
        flexGrow: `2`,
        flexBasis: `100px`,
        isSearchable: true,
        isSortable: false,
        cellRenderer: (entity: RowEntity) => {
          return (
            <div sel='.creator-icon'>
              <BrowseCreatorWithViews entity={entity.value.searchResult} />
            </div>
          );
        },
      },
    ];
  }

  onRowSelect(ev) {
    ev.stopPropagation();
    const row: RowEntity = ev.detail.row;
    this.params.onEntityClick?.(row.value.searchResult);

    const url = row.link;
    if (shouldOpenInNewTab(ev)) {
      window.open(url, `_blank`);
    } else if (this.params.mode === BrowseMode.Add) {
      this.params.onEntitySelect?.(row.value.searchResult);
    } else {
      this.urlManager.setLocation(url);
      this.params.onEntityRedirect?.();
    }
  }

  dispatchTrackEvent({eventName, trackingProps = {}}) {
    this.dispatchEvent(
      new CustomEvent(`trackEvent`, {
        detail: {
          eventName,
          trackingProps,
        },
      }),
    );
  }

  getCurrentProject() {
    return this.getContext(`currentProject`).get().value;
  }

  getTableRows(searchResult: SearchResult[]): RowEntity[] {
    searchResult = searchResult || [];

    return searchResult.map(
      (item): RowEntity => {
        const link =
          this.params.mode === BrowseMode.Discover && item.type !== SearchResultType.Dashboard
            ? appendBrowseSource(item.link, this.getContext(`urlManager`))
            : item.link;
        const lastEditedMoment = moment(item.last_edited);
        const isEntitySharedWithUser = item.shared_with_user;
        const showDraftBadge =
          item.type === SearchResultType.Dashboard && item.generation_type === DashboardType.StagedTemplate;
        return {
          rowType: item.type,
          name: item.name,
          dashboard: item.dashboard,
          description: item.description,
          displayContext: getDisplayContext(item),
          disableUserSidebar: true,
          showDraftBadge,
          lastModified: item.last_edited,
          lastModifiedDisplay: lastEditedMoment.format(`MMM DD, YYYY`),
          created: item.created,
          link,
          popularityScore: item.scores.popularity,
          finalScore: item.scores.final,
          isRowClickDisabled: !isEntitySharedWithUser,
          presenceViews: (item.presence_views || []).map(({user, timestamp}) => {
            return {
              id: user.id,
              email: user.email,
              name: user.name,
              lastViewedUtcSeconds: timestamp,
            };
          }),
          report: item.report,
          uniqueCount: item.unique_count,
          user: item.user,
          userId: item.user_id,
          userEmail: item.email,
          value: {
            searchResult: item,
            // to make bookmark deletion and selection work
            id: item.id,
            title: item.name,
            userId: item.user_id,
            rowType: item.type,
            report: item.report,
          },
        };
      },
    );
  }

  get urlManager() {
    return this.getContext(`urlManager`);
  }
}

export const EntityTableEl = defineAndMakeJsxFactory(`entity-table-widget`, EntityTable);
export const EntityTableReact = panel2react(`entity-table-widget`, EntityTable);
